import { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from '../global/GlobalContext';
import { UserRole } from '../config/userRole';

const isDistributor = () => UserRole.userRole === 'distributor';

/**
 * The single shared set of wishlist item actions — add-to-cart (with an
 * optional size step, and a required quantity step for distributors) and
 * remove — used by both the /wishlist page and the WishlistSlider embedded
 * on the Cart page.
 *
 * A wishlist entry is originally saved against one specific variation, but
 * the underlying product may have other sizes available — so add-to-cart
 * offers a size picker whenever more than one size exists. Quantity is
 * required for distributor accounts (must be an explicit multiple of the
 * pack size) — no default quantity is ever silently assumed.
 */
export function useWishlistAddToCart({ navigateOnSuccess = true } = {}) {
  const { addToCart, token, setWishlist } = useGlobal();
  const navigate = useNavigate();

  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [isSizePopupOpen, setIsSizePopupOpen] = useState(false);
  const [isQtyPopupOpen, setIsQtyPopupOpen] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(null);

  const getSizeOptions = (item) => {
    const product = item?.product || item;
    const variations = Array.isArray(product?.product_variations) ? product.product_variations : [];
    const withSize = variations.filter((v) => v?.size && String(v.size).trim() !== '');
    const seen = new Set();
    return withSize.filter((v) => {
      const key = v.size_id ?? v.size;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getItemPackQty = (item, variationOverride) => {
    if (!item) return 10;
    const product = item?.product || item;
    const variation = variationOverride || item?.product_variation || null;
    const packQty = Number(variation?.pack_qty || product?.pack_qty || 0);
    return packQty > 0 ? packQty : 10;
  };

  const addItemToCart = async (item, quantity, variationOverride) => {
    const product = item?.product || item;
    if (!product || !product.id) {
      console.error('Invalid wishlist product:', item);
      return;
    }
    const variation = variationOverride || item?.product_variation || null;
    const productForCart = variation
      ? { ...product, product_variations: [variation] }
      : product;

    setAddingId(item.id);
    try {
      const success = await addToCart(productForCart, variation?.id || null, quantity);
      if (success) {
        if (navigateOnSuccess) navigate('/cart');
      } else {
        Swal.fire({
          title: 'Could not add to cart',
          text: 'Something went wrong adding this item. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: 'rgb(var(--brand-600))',
        });
      }
      return success;
    } catch (error) {
      console.error('Error adding to cart:', error);
      Swal.fire({
        title: 'Could not add to cart',
        text: 'Something went wrong adding this item. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: 'rgb(var(--brand-600))',
      });
      return false;
    } finally {
      setAddingId(null);
    }
  };

  const openQtyPopupOrAdd = (item, variationOverride) => {
    if (isDistributor()) {
      setSelectedQuantity(null);
      setIsQtyPopupOpen(true);
    } else {
      addItemToCart(item, 1, variationOverride);
    }
  };

  const handleAddToCartClick = (item) => {
    const sizeOptions = getSizeOptions(item);
    setCurrentItem(item);
    setSelectedVariation(null);

    if (sizeOptions.length > 1) {
      setIsSizePopupOpen(true);
      return;
    }

    openQtyPopupOrAdd(item, item?.product_variation || null);
  };

  const closeSizePopup = () => {
    setIsSizePopupOpen(false);
    setSelectedVariation(null);
  };

  const confirmSize = () => {
    if (!currentItem || !selectedVariation) return;
    setIsSizePopupOpen(false);
    openQtyPopupOrAdd(currentItem, selectedVariation);
  };

  const closeQtyPopup = () => {
    setIsQtyPopupOpen(false);
    setSelectedQuantity(null);
  };

  const confirmQuantity = async () => {
    if (!currentItem || !selectedQuantity) return;
    const item = currentItem;
    const qty = selectedQuantity;
    const variation = selectedVariation;
    closeQtyPopup();
    await addItemToCart(item, qty, variation);
  };

  const popupQuantities = Array.from(
    { length: 10 },
    (_, i) => (i + 1) * getItemPackQty(currentItem, selectedVariation)
  );

  const sizeOptions = getSizeOptions(currentItem);

  const removeFromWishlist = async (itemId) => {
    const result = await Swal.fire({
      title: 'Remove item?',
      text: 'This item will be removed from your wishlist.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'rgb(var(--brand-600))',
    });
    if (!result.isConfirmed) return;

    setRemovingId(itemId);
    try {
      await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/delete_wishlist_item/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWishlist((prevItems) => prevItems.filter((item) => item.id !== itemId));
    } catch (error) {
      console.error('Error removing item from wishlist:', error);
      Swal.fire({
        title: 'Could not remove item',
        text: 'Something went wrong. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: 'rgb(var(--brand-600))',
      });
    } finally {
      setRemovingId(null);
    }
  };

  return {
    addingId,
    removingId,
    currentItem,
    isSizePopupOpen,
    sizeOptions,
    selectedVariation,
    setSelectedVariation,
    closeSizePopup,
    confirmSize,
    isQtyPopupOpen,
    selectedQuantity,
    setSelectedQuantity,
    popupQuantities,
    getItemPackQty,
    closeQtyPopup,
    confirmQuantity,
    handleAddToCartClick,
    removeFromWishlist,
  };
}
